import {
	IMAGE_SERVER_NORMALIZATION_EXTENSIONS,
	IMAGE_SERVER_NORMALIZATION_MIME_TYPES,
} from '@run-diffusion/shared';

/**
 * True for formats no browser can display, which must take the normalizing
 * POST path so the server can re-encode them to JPEG. Everything else is
 * stored byte for byte over the signed-URL path.
 *
 * Named for what it decides now: it used to gate a client-side resize as well,
 * which IMG-2026-096 deleted.
 */
export const requiresServerNormalization = (file: File): boolean => {
	const extension: string = file.name.split('.').pop()?.toLowerCase() || '';
	const mimeType: string = file.type?.toLowerCase() || '';

	return (
		IMAGE_SERVER_NORMALIZATION_EXTENSIONS.includes(extension) ||
		IMAGE_SERVER_NORMALIZATION_MIME_TYPES.includes(mimeType)
	);
};

/**
 * Every image extension that can reach an upload, by either route: the picker
 * (jpg/jpeg/png/webp, plus the normalized formats) or a URL drag, which can
 * hand us any of them.
 */
const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = Object.assign(Object.create(null), {
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	png: 'image/png',
	webp: 'image/webp',
	heic: 'image/heic',
	heif: 'image/heif',
	tif: 'image/tiff',
	tiff: 'image/tiff',
	bmp: 'image/bmp',
// Null-prototype: these are indexed by untrusted strings, and a plain literal
// resolves "constructor" or "toString" to a function that then flows into a
// File's type and the upload payload.
});

/** Inverse of the above, for naming bytes that arrived without a file name. */
const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = Object.assign(Object.create(null), {
	'image/jpeg': 'jpg',
	'image/png': 'png',
	'image/webp': 'webp',
	'image/heic': 'heic',
	'image/heif': 'heif',
	'image/tiff': 'tiff',
	'image/bmp': 'bmp',
});

const extensionFromUrl = (url: string): string => {
	// Strip the query and fragment first, or "photo.jpg?v=2" yields "jpg?v=2".
	const path: string = (url || '').split('#')[0].split('?')[0];
	const lastSegment: string = path.split('/').pop() || '';
	if (!lastSegment.includes('.')) return '';
	return lastSegment.split('.').pop()?.toLowerCase() || '';
};

/**
 * Build a File for image bytes that arrived without a name, such as an image
 * dragged in by URL.
 *
 * The name and the type are not cosmetic here. requiresServerNormalization
 * routes on them, and on the signed path resolveUploadContentType writes the
 * type straight onto the stored object with nothing downstream to correct it.
 * This used to label every dragged blob `dragged-image.png` / `image/png`,
 * which stored JPEG bytes as PNG and sent a dragged HEIC down the verbatim
 * path, where nothing converts it and the result is undisplayable.
 *
 * When the response carries no type and the URL has no usable extension, this
 * deliberately returns octet-stream rather than guessing: an honest refusal
 * downstream beats a confident mislabel.
 */
export const fileFromImageBlob = (blob: Blob, sourceUrl: string, baseName: string = 'dragged-image'): File => {
	const reported: string = (blob.type || '').toLowerCase().split(';')[0].trim();
	const urlExtension: string = extensionFromUrl(sourceUrl);
	const knownUrlExtension: string = CONTENT_TYPE_BY_EXTENSION[urlExtension] ? urlExtension : '';
	const contentType: string = reported || CONTENT_TYPE_BY_EXTENSION[urlExtension] || 'application/octet-stream';
	const extension: string = EXTENSION_BY_CONTENT_TYPE[contentType] || knownUrlExtension;
	const fileName: string = extension ? `${baseName}.${extension}` : baseName;
	return new File([blob], fileName, { type: contentType });
};

/**
 * The content type to store an upload under.
 *
 * The POST path read the real format out of sharp's metadata. The signed path
 * has no server-side decode at all: whatever this returns is what lands on the
 * GCS object and in the Firestore record, and an object served as
 * application/octet-stream will not render in an <img>. Browsers report an
 * empty or octet-stream File.type often enough (several drag sources do) that
 * the extension has to cover for it, and the picker admits those files on the
 * extension branch of isFileAccepted.
 */
export const resolveUploadContentType = (file: File): string => {
	const reported: string = file.type?.trim().toLowerCase() || '';
	if (reported && reported !== 'application/octet-stream') return reported;
	const extension: string = file.name.split('.').pop()?.toLowerCase() || '';
	return CONTENT_TYPE_BY_EXTENSION[extension] || 'application/octet-stream';
};

/** Longest edge of the copy handed to the NSFW classifier. */
export const NSFW_SCREENING_PROXY_MAX_EDGE: number = 1024;

export const proxyDimensionsFor = (sourceWidth: number, sourceHeight: number): { width: number, height: number } => {
	const longestEdge: number = Math.max(sourceWidth, sourceHeight);
	const scale: number = Math.min(1, NSFW_SCREENING_PROXY_MAX_EDGE / longestEdge);
	return {
		width: Math.max(1, Math.round(sourceWidth * scale)),
		height: Math.max(1, Math.round(sourceHeight * scale)),
	};
};

/**
 * The destination canvas is always proxy-sized, never source-sized, which is
 * what keeps this inside iOS Safari's ~16.7M pixel canvas area cap for any
 * input. drawImage does the downsample.
 */
const drawToProxyCanvas = (source: CanvasImageSource, sourceWidth: number, sourceHeight: number): Promise<Blob> => {
	const { width, height } = proxyDimensionsFor(sourceWidth, sourceHeight);
	const canvas: HTMLCanvasElement = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	const ctx: CanvasRenderingContext2D = canvas.getContext('2d');
	if (!ctx) throw new Error('Canvas 2D context unavailable');
	ctx.drawImage(source, 0, 0, width, height);
	return new Promise<Blob>((resolve, reject) => {
		canvas.toBlob(
			(blob: Blob) => (blob ? resolve(blob) : reject(new Error('Screening proxy encode failed'))),
			'image/jpeg',
			0.85,
		);
	});
};

/**
 * Neither load nor error is guaranteed to fire: a decoder can stall on a very
 * large or malformed image and simply never settle, which would leave the NSFW
 * promise pending, the spinner running and the drop zone locked for the rest of
 * the session. The annotation composite path bounds the same hazard.
 */
const IMAGE_DECODE_TIMEOUT_MS: number = 30000;

const decodeViaImageElement = (file: File): Promise<HTMLImageElement> => {
	return new Promise((resolve, reject) => {
		const img: HTMLImageElement = new Image();
		const tempUrl: string = URL.createObjectURL(file);
		let settled: boolean = false;
		const finish = (run: () => void) => {
			if (settled) return;
			settled = true;
			clearTimeout(timeoutId);
			URL.revokeObjectURL(tempUrl);
			run();
		};
		const timeoutId = setTimeout(
			() => finish(() => reject(new Error(`Image decode timed out after ${Math.round(IMAGE_DECODE_TIMEOUT_MS / 1000)}s`))),
			IMAGE_DECODE_TIMEOUT_MS,
		);
		img.onload = () => finish(() => resolve(img));
		img.onerror = () => finish(() => reject(new Error('Error loading image')));
		img.src = tempUrl;
	});
};

/**
 * A small JPEG copy of a chosen file, built solely so the NSFW classifier has
 * something to classify. Never stored and never shown.
 *
 * The classifier takes its input inline as base64, so a large file would exceed
 * Cloud Run's request cap and, because the caller resolves false on error, would
 * upload unscreened. The classifier downsamples internally regardless, so a
 * proxy is the same check rather than a weaker one.
 *
 * createImageBitmap is preferred (no full-size canvas, and no layout work), with
 * an <img> decode as a second path for browsers that lack it or reject a given
 * blob. Its resizeWidth/resizeHeight options are deliberately not used: WebKit
 * ignores them (bug 202458), so relying on them would make the output size
 * depend on the engine. Both paths draw into the same proxy-sized canvas.
 *
 * Throws for a format the browser cannot decode at all (HEIC outside Safari,
 * TIFF almost everywhere). Callers decide what that means; see the policy in
 * ImageUpload.isNsfw.
 */
export const buildScreeningProxy = async (file: File): Promise<Blob> => {
	let bitmap: ImageBitmap | null = null;
	if (typeof createImageBitmap === 'function') {
		try {
			bitmap = await createImageBitmap(file);
		} catch (err) {
			// Only the decode is guarded here. Wrapping the canvas work too would
			// re-decode the whole file through an <img> because toBlob failed,
			// doubling peak memory on a 100MB image and then failing the same way.
			console.warn('createImageBitmap unavailable for this file; falling back to an image decode', err);
		}
	}
	if (bitmap) {
		try {
			return await drawToProxyCanvas(bitmap, bitmap.width, bitmap.height);
		} finally {
			bitmap.close();
		}
	}
	const img: HTMLImageElement = await decodeViaImageElement(file);
	return await drawToProxyCanvas(img, img.naturalWidth, img.naturalHeight);
};
